import client from 'client'
import type { GetAssetListAssetGetQueryResponse, GetAssetListAssetGetQueryParams, GetAssetListAssetGet422 } from '../../types/GetAssetListAssetGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getAssetListAssetGetQueryKey = (params?: GetAssetListAssetGetQueryParams) => [{ url: '/asset' }, ...(params ? [params] : [])] as const

export type GetAssetListAssetGetQueryKey = ReturnType<typeof getAssetListAssetGetQueryKey>

/**
 * @description Retrieve a list of assets based on filtering criteria.**Company ID Parameter:**- **Company ID string**: View assets for a specific company- **"NONE"**: View assets with no company assignment- **"ALL"**: View assets across all companies (requires superadmin privileges)The "ALL" token allows superadmins to view assets across all companies in the system.Regular users can only access assets for companies they have permissions for.**Filtering:**Assets can be filtered by scheme, asset type, position assignment, and text search.Results are paginated using cursor-based pagination.
 * @summary Get asset list
 * {@link /asset}
 */
export async function getAssetListAssetGet(params?: GetAssetListAssetGetQueryParams, config: Partial<RequestConfig> & { client?: typeof client } = {}) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<GetAssetListAssetGetQueryResponse, ResponseErrorConfig<GetAssetListAssetGet422>, unknown>({
    method: 'GET',
    url: `/asset`,
    params,
    ...requestConfig,
  })
  return res
}

export function getAssetListAssetGetQueryOptions(params?: GetAssetListAssetGetQueryParams, config: Partial<RequestConfig> & { client?: typeof client } = {}) {
  const queryKey = getAssetListAssetGetQueryKey(params)
  return queryOptions<
    ResponseConfig<GetAssetListAssetGetQueryResponse>,
    ResponseErrorConfig<GetAssetListAssetGet422>,
    ResponseConfig<GetAssetListAssetGetQueryResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getAssetListAssetGet(params, config)
    },
  })
}

/**
 * @description Retrieve a list of assets based on filtering criteria.**Company ID Parameter:**- **Company ID string**: View assets for a specific company- **"NONE"**: View assets with no company assignment- **"ALL"**: View assets across all companies (requires superadmin privileges)The "ALL" token allows superadmins to view assets across all companies in the system.Regular users can only access assets for companies they have permissions for.**Filtering:**Assets can be filtered by scheme, asset type, position assignment, and text search.Results are paginated using cursor-based pagination.
 * @summary Get asset list
 * {@link /asset}
 */
export function useGetAssetListAssetGet<
  TData = ResponseConfig<GetAssetListAssetGetQueryResponse>,
  TQueryData = ResponseConfig<GetAssetListAssetGetQueryResponse>,
  TQueryKey extends QueryKey = GetAssetListAssetGetQueryKey,
>(
  params?: GetAssetListAssetGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<ResponseConfig<GetAssetListAssetGetQueryResponse>, ResponseErrorConfig<GetAssetListAssetGet422>, TData, TQueryData, TQueryKey>
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getAssetListAssetGetQueryKey(params)

  const query = useQuery({
    ...(getAssetListAssetGetQueryOptions(params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetAssetListAssetGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}